import { Checkbox, Radio, Input as InputLib } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import * as yup from "yup";
import { useLocation } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";

import { Button, Input, Select } from "components";
import { theme } from "theme/theme";
import { PatientRegistrationActionCreators } from "redux/rootActions";
import {
  selectIsRegisteringPatient,
  selectPatientRegistrationErrorMessage,
  patientRegistrationDataResponse,
} from "redux/setting/patientRegistration/patientRegistrationStates";
import { httpStatus } from "configs/httpStatus";
import { ErrorMessage, WrapperErrorMessage } from "theme/CommonStyle";
import { CheckboxStyledExpand } from "screens/settings/Setting.style";

import { Title } from "./NursingHomeRegistration";
import moment from "moment";

const currentYear = moment().year();
const yearOptions: any = [];
for (let i = 1900; i <= currentYear; i++) {
  yearOptions.push({
    id: i,
    value: i,
    name: i,
  });
}

const currentMonth = moment().month() + 1;
const monthOptionsDefault: any = [];
for (let i = 1; i <= 12; i++) {
  if (i <= 9) {
    monthOptionsDefault.push({
      id: i,
      value: i,
      name: `0${i}`,
    });
  } else {
    monthOptionsDefault.push({
      id: i,
      value: i,
      name: i,
    });
  }
}

const currentDate = moment().date();
const dayOptionsDefault: any = [];
for (let i = 1; i <= 31; i++) {
  if (i <= 9) {
    dayOptionsDefault.push({
      id: i,
      value: i,
      name: `0${i}`,
    });
  } else {
    dayOptionsDefault.push({
      id: i,
      value: i,
      name: i,
    });
  }
}

const AudioWrapper = styled.div`
  flex: 1;
  display: flex;
  gap: 20px;
  flex-direction: column;
  .ant-radio-wrapper {
    height: max-content;
    align-items: center;
    display: flex;
  }
  .ant-radio-wrapper::after {
    display: none;
  }
  .ant-radio-wrapper > span:not(.ant-radio) {
    font-size: 16px;
    line-height: 19.5px;
    font-weight: 500;
    padding-left: 6px;
    padding-right: 0;
  }
  .ant-radio {
    top: 0;
  }
  .ant-radio-inner {
    border: 2px solid #cccccc;
    width: 20px;
    height: 20px;
  }
  .ant-radio-checked .ant-radio-inner {
    border: 2px solid #cccccc;
    width: 20px;
    height: 20px;
  }
  .ant-radio-inner::after {
    background-color: #2ac769;
    top: calc(50% - 6px);
    left: calc(50% - 6px);
    width: 12px;
    height: 12px;
  }
  .ant-radio:hover .ant-radio-inner,
  .ant-radio:hover .ant-radio-checked .ant-radio-inner {
    border-color: #cccccc;
  }

  ${theme.media.tablet} {
    gap: 18px;
    flex-direction: row;
  }
`;

const WrapperSelect = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 32px;
`;

const Label = styled.div`
  font-size: 14px;
  line-height: 14px;
  font-weight: 500;
  margin-bottom: 8px;

  span {
    color: #fb4e4e;
  }
`;

const LabelSpecial = styled(Label)`
  color: #1a2f62;
`;

const Text = styled.div`
  font-family: Noto Sans;
  font-weight: 500;
  font-size: 12px;
  line-height: 22px;
  letter-spacing: 0px;
  color: #1a2f62;
`;

const SecondText = styled(Text)`
  color: #666666;
`;

const ThirdText = styled(Text)`
  font-weight: 700;
  margin-bottom: 2px;
  ${theme.media.tablet} {
    margin-bottom: 0px;
  }
`;

const WrapperCheckbox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  .ant-checkbox-wrapper {
    margin-left: 0;
    align-items: center;
  }
`;

export const WrapperInput = styled.div`
  margin-bottom: 8px;

  .ant-input-affix-wrapper {
    border-radius: 6px;
    height: 40px;
    outline: none;
    box-shadow: inset 0px 2px 4px rgba(0, 0, 0, 0.09);
    border: 1px solid ${theme.colors.border};

    &:hover {
      border-color: ${theme.colors.border};
    }

    &:focus {
      border: 1px solid rgb(131, 188, 226);
    }
  }
`;

const PasswordUsageText = styled.div`
  font-weight: 500;
  font-size: 11px;
  line-height: 14px;
  letter-spacing: 0px;
  color: #e89806;
  margin-bottom: 2px;
  ${theme.media.tablet} {
    line-height: 22px;
  }
`;

const enoughCharacter = /^.{10,48}$/;
const haveNumber = /\d/;
const haveUppercase = /[A-Z]/;
const haveLowercase = /[a-z]/;
// eslint-disable-next-line no-useless-escape
const haveSpecialCharacter = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/;
export const EMAIL_REGEX =
  /^([a-zA-Z0-9_\.\-])+\@(([a-zA-Z0-9\-])+\.)+([a-zA-Z0-9]{2,4})+$/; /* eslint-disable-line */

const schemaDefault = {
  password: yup.string().required("パスワードを入力してください"),
};

const schemaFullForm = {
  email: yup
    .string()
    .trim()
    .required("メールアドレスを入力してください")
    .matches(EMAIL_REGEX, "メールアドレスのフォーマットが正しくありません"),
  lastName: yup.string().required("姓を入力してください"),
  firstName: yup.string().required("名を入力してください"),
  confirmPassword: yup
    .string()
    .required("確認用のパスワードを入力してください")
    .oneOf([yup.ref("password")], "パスワードが一致しません"),
  sex: yup.string().required("性別を入力してください"),
  year: yup.string().required("を入力してください"),
  month: yup.string().required("を入力してください"),
  day: yup.string().required("を入力してください"),
};

const refactorIsVerifyIdentityCode = (isVerifyIdentityCode: string) => {
  if (!isVerifyIdentityCode) return "false";
  if (isVerifyIdentityCode === "true" || isVerifyIdentityCode.startsWith("t")) {
    return "true";
  } else if (
    isVerifyIdentityCode === "false" ||
    isVerifyIdentityCode.startsWith("f")
  ) {
    return "false";
  } else {
    return "false";
  }
};

const AccountRegistration: React.FC = () => {
  const { search } = useLocation();
  const phone: any = "+" + new URLSearchParams(search).get("phone")?.slice(1);
  const superCityName: any = new URLSearchParams(search).get("name");
  const superCityCode: any = new URLSearchParams(search).get("super-city-code");
  const identityCodeTitle: any = new URLSearchParams(search).get(
    "identity-code-title"
  );
  const isVerifyIdentityCode: any = JSON.parse(
    refactorIsVerifyIdentityCode(
      new URLSearchParams(search).get("is-verify-identity-code") as any
    )
  );

  const dispatch = useDispatch();
  const registeringPatient = useSelector(selectIsRegisteringPatient);
  const registerPatientError = useSelector(
    selectPatientRegistrationErrorMessage
  );
  const patientRegistrationData = useSelector(patientRegistrationDataResponse);

  const typeForm = localStorage.getItem("type_form") || "full";

  const [passwordVisible, setPasswordVisible] = useState<any>(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [dayOptions, setDayOptions] = useState(dayOptionsDefault);
  const [monthOptions, setMonthOptions] = useState(monthOptionsDefault);
  const [apiError, setApiError] = useState<string>("");

  const schemaShortForm = isVerifyIdentityCode
    ? {
        ...schemaDefault,
        codeTitle: yup
          .string()
          .required(`${identityCodeTitle || "登録番号"}を入力してください`),
      }
    : schemaDefault;

  const { watch, handleSubmit, control, setValue, setError } = useForm({
    resolver: yupResolver(
      yup.object().shape(
        typeForm === "short"
          ? schemaShortForm
          : {
              ...schemaShortForm,
              ...schemaFullForm,
            }
      )
    ),
    defaultValues: {
      email: "",
      phone: phone,
      lastName: "",
      firstName: "",
      confirmPassword: "",
      password: "",
      codeTitle: "",
      sex: 0,
      year: 1990,
      month: 1,
      day: 1,
      condition: [],
    },
  });

  const codeTitleWatch = watch("codeTitle");
  const emailWatch = watch("email");
  const monthWatch = watch("month");
  const yearWatch = watch("year");
  const watchPassword = watch("password");

  const daysOfMonth = useMemo<number>(() => {
    return moment(`${yearWatch}-${monthWatch}`, "YYYY-MM").daysInMonth();
  }, [yearWatch, monthWatch]);

  const handleOptions = (num: number, current?: any) => {
    const arr: any = [];

    for (let i = 1; i <= num; i++) {
      if (i <= 9) {
        let item: any = {
          id: i,
          value: i,
          name: `0${i}`,
        };
        if (current) {
          item = {
            ...item,
            disabled: current >= i ? false : true,
          };
        }
        arr.push(item);
      } else {
        let item: any = {
          id: i,
          value: i,
          name: i,
        };
        if (current) {
          item = {
            ...item,
            disabled: current >= i ? false : true,
          };
        }
        arr.push(item);
      }
    }

    return arr;
  };

  useEffect(() => {
    let monthClone: any = [];
    let dayClone: any = [];

    if (currentYear > Number(yearWatch)) {
      monthClone = handleOptions(12);
      dayClone = handleOptions(daysOfMonth);
    } else {
      if (currentMonth > Number(monthWatch)) {
        dayClone = handleOptions(daysOfMonth);
      } else {
        dayClone = handleOptions(daysOfMonth, currentDate);
      }

      monthClone = handleOptions(12, currentMonth);
    }

    setValue("month", 1);
    setValue("day", 1);
    setDayOptions(dayClone);
    setMonthOptions(monthClone);
  }, [yearWatch]);

  useEffect(() => {
    let dayClone: any = [];

    if (currentMonth > Number(monthWatch)) {
      dayClone = handleOptions(daysOfMonth);
    } else {
      if (currentYear === Number(yearWatch)) {
        dayClone = handleOptions(daysOfMonth, currentDate);
      } else {
        dayClone = handleOptions(daysOfMonth);
      }
    }

    setDayOptions(dayClone.length > 0 ? dayClone : dayOptions);
    setValue("day", 1);
  }, [monthWatch]);

  useEffect(() => {
    if (isVerifyIdentityCode) {
      setError("codeTitle", { message: "" });
    }
  }, [codeTitleWatch]);

  useEffect(() => {
    setError("email", { message: "" });
  }, [emailWatch]);

  const onSubmit = (data: any) => {
    let params: any = {
      password: data.password,
      mobile_number: phone,
      super_city_code: superCityCode,
    };

    if (isVerifyIdentityCode) {
      params = {
        ...params,
        identity_code: data.codeTitle,
      };
    }

    if (typeForm === "full") {
      params = {
        ...params,
        email: data.email,
        first_name: data.firstName,
        last_name: data.lastName,
        gender: Number(data.sex),
        date_of_birth: `${data.day}/${data.month}/${data.year}`,
        password_confirmation: data.confirmPassword,
      };
    }

    dispatch(PatientRegistrationActionCreators.registerPatientAction(params));
  };

  const errorMessage: any = {
    identity_code_not_found_or_used: "入力したコードは正しくありません。",
    password_is_not_correct: "パスワードが誤っています。",
  };

  const errorKey: any = {
    identity_code_not_found_or_used: "codeTitle",
    password_is_not_correct: "password",
  };

  useEffect(() => {
    if (patientRegistrationData) {
      if (patientRegistrationData.status === httpStatus.StatusOK) {
        dispatch(PatientRegistrationActionCreators.handleResetAction());
        const newURL = `/download-app-qr?name=${superCityName}`;
        window.location.replace(newURL);
      } else {
        const res = patientRegistrationData?.result || "";
        const mes = patientRegistrationData?.message || "";

        if (res.length) {
          setError(errorKey[res], {
            message: errorMessage[res],
          });
          if (
            res === "identity_code_not_found_or_used" &&
            !isVerifyIdentityCode
          ) {
            setApiError("固有番号が見つからないか、使用されています。");
          }
        } else {
          if (mes.length) {
            setError("email", {
              message: mes,
            });
          }
        }

        dispatch(PatientRegistrationActionCreators.handleResetAction());
      }
    }
  }, [patientRegistrationData]);

  useEffect(() => {
    const arr: any = [];

    if (enoughCharacter.test(watchPassword)) {
      if (!arr.includes("enoughCharacter")) {
        arr.push("enoughCharacter");
      }
    } else {
      const idx = arr.indexOf("enoughCharacter");
      if (idx > -1) {
        arr.splice(idx, 1, "enoughCharacter");
      }
    }

    if (haveNumber.test(watchPassword)) {
      if (!arr.includes("haveNumber")) {
        arr.push("haveNumber");
      }
    } else {
      const idx = arr.indexOf("haveNumber");
      if (idx > -1) {
        arr.splice(idx, 1, "haveNumber");
      }
    }

    if (haveUppercase.test(watchPassword)) {
      if (!arr.includes("haveUppercase")) {
        arr.push("haveUppercase");
      }
    } else {
      const idx = arr.indexOf("haveUppercase");
      if (idx > -1) {
        arr.splice(idx, 1, "haveUppercase");
      }
    }

    if (haveLowercase.test(watchPassword === undefined ? "" : watchPassword)) {
      if (!arr.includes("haveLowercase")) {
        arr.push("haveLowercase");
      }
    } else {
      const idx = arr.indexOf("haveLowercase");
      if (idx > -1) {
        arr.splice(idx, 1, "haveLowercase");
      }
    }

    if (haveSpecialCharacter.test(watchPassword)) {
      if (!arr.includes("haveSpecialCharacter")) {
        arr.push("haveSpecialCharacter");
      }
    } else {
      const idx = arr.indexOf("haveSpecialCharacter");
      if (idx > -1) {
        arr.splice(idx, 1, "haveSpecialCharacter");
      }
    }

    let isActive = false;
    if (arr.length === 4) {
      if (!arr.includes("haveSpecialCharacter")) {
        isActive = true;
      } else {
        isActive = false;
      }
    }

    if (arr.length === 5) {
      isActive = true;
    }

    setError("password", { message: "" });
    setIsDisabled(isActive);
    setValue("condition", arr);
  }, [watchPassword]);

  useEffect(() => {
    if (registerPatientError && !registeringPatient) {
      dispatch(PatientRegistrationActionCreators.handleResetAction());
    }
  }, [registerPatientError, registeringPatient]);

  useEffect(() => {
    if (apiError) {
      const timeout = setTimeout(() => {
        setApiError("");
        clearTimeout(timeout);
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [apiError]);

  return (
    <div>
      <Title>{superCityName}登録</Title>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Input
          label="現在ご利用中の携帯電話番号"
          height={40}
          marginForm="0 0 24px"
          fsLabel={14}
          fwLabel={500}
          disabled
          value={phone}
        />

        {isVerifyIdentityCode && (
          <>
            <Label>
              {identityCodeTitle.length > 0 ? identityCodeTitle : "登録番号"}
              <span>*</span>
            </Label>
            <Controller
              control={control}
              name="codeTitle"
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <Input
                  height={40}
                  marginForm="0 0 24px"
                  fsLabel={14}
                  fwLabel={500}
                  onChange={onChange}
                  error={error?.message}
                  value={value}
                />
              )}
            />
          </>
        )}

        {typeForm === "full" && (
          <>
            <Label>
              姓<span>*</span>
            </Label>
            <Controller
              control={control}
              name="lastName"
              render={({ field: { onChange }, fieldState: { error } }) => (
                <Input
                  height={40}
                  marginForm="0 0 24px"
                  fsLabel={14}
                  fwLabel={500}
                  placeholder="リーバー"
                  onChange={onChange}
                  error={error?.message}
                  autoComplete="one-time-code"
                />
              )}
            />

            <Label>
              名<span>*</span>
            </Label>
            <Controller
              control={control}
              name="firstName"
              render={({ field: { onChange }, fieldState: { error } }) => (
                <Input
                  height={40}
                  marginForm="0 0 24px"
                  fsLabel={14}
                  fwLabel={500}
                  placeholder="太郎"
                  onChange={onChange}
                  error={error?.message}
                  autoComplete="one-time-code"
                />
              )}
            />

            <Label>
              メールアドレス<span>*</span>
            </Label>
            <Controller
              control={control}
              name="email"
              render={({ field: { onChange }, fieldState: { error } }) => (
                <Input
                  height={40}
                  marginForm="0 0 24px"
                  fsLabel={14}
                  fwLabel={500}
                  placeholder="info@leber.jp"
                  onChange={onChange}
                  error={error?.message}
                  autoComplete="one-time-code"
                />
              )}
            />
          </>
        )}

        <LabelSpecial>
          パスワード<span>*</span>
        </LabelSpecial>
        <Controller
          control={control}
          name="password"
          render={({ field: { onChange }, fieldState: { error } }) => (
            <div style={{ position: "relative" }}>
              <WrapperInput>
                <InputLib.Password
                  autoComplete="one-time-code"
                  onChange={onChange}
                  visibilityToggle={
                    {
                      visible: passwordVisible,
                      onVisibleChange: setPasswordVisible,
                    } as any
                  }
                />
              </WrapperInput>
              {error?.message && (
                <WrapperErrorMessage>
                  <ErrorMessage>{error?.message}</ErrorMessage>
                </WrapperErrorMessage>
              )}
            </div>
          )}
        />

        {typeForm === "full" && (
          <>
            <ThirdText>パスワード条件：</ThirdText>
            <PasswordUsageText>
              ※パスワードは、推奨以外の4つの条件を全て満たす必要があります。
            </PasswordUsageText>
            <div style={{ pointerEvents: "none" }}>
              <Controller
                control={control}
                name="condition"
                render={({ field: { onChange, value } }) => (
                  <Checkbox.Group
                    style={{ width: "100%", marginBottom: 24 }}
                    onChange={onChange}
                    value={value}
                  >
                    <WrapperCheckbox>
                      <CheckboxStyledExpand value="enoughCharacter">
                        <Text>10〜48文字以上</Text>
                      </CheckboxStyledExpand>
                      <CheckboxStyledExpand value="haveNumber">
                        <Text>数字を含む</Text>
                      </CheckboxStyledExpand>
                      <CheckboxStyledExpand value="haveUppercase">
                        <Text>英大文字を含む</Text>
                      </CheckboxStyledExpand>
                      <CheckboxStyledExpand value="haveLowercase">
                        <Text>英小文字を含む</Text>
                      </CheckboxStyledExpand>
                      <CheckboxStyledExpand value="haveSpecialCharacter">
                        <SecondText>記号を含む（推奨）</SecondText>
                      </CheckboxStyledExpand>
                    </WrapperCheckbox>
                  </Checkbox.Group>
                )}
              />
            </div>

            <LabelSpecial>
              パスワード（確認）<span>*</span>
            </LabelSpecial>
            <Controller
              control={control}
              name="confirmPassword"
              render={({ field: { onChange }, fieldState: { error } }) => (
                <Input
                  type="password"
                  height={40}
                  marginForm="0 0 24px"
                  fsLabel={14}
                  fwLabel={500}
                  onChange={onChange}
                  error={error?.message}
                  autoComplete="one-time-code"
                />
              )}
            />

            <Label>性別</Label>
            <Controller
              control={control}
              name="sex"
              render={({ field: { onChange, value } }) => (
                <Radio.Group
                  onChange={onChange}
                  value={value}
                  style={{ marginBottom: 24 }}
                >
                  <AudioWrapper>
                    <Radio value={0}>男性</Radio>
                    <Radio value={1}>女性</Radio>
                    <Radio value={2}>その他</Radio>
                  </AudioWrapper>
                </Radio.Group>
              )}
            />

            <Label>生年月日</Label>
            <WrapperSelect>
              <Controller
                control={control}
                name="year"
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <Select
                    height="30px"
                    width="78px"
                    onChange={onChange}
                    error={error?.message}
                    options={yearOptions}
                    value={value}
                  />
                )}
              />
              <Controller
                control={control}
                name="month"
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <Select
                    height="30px"
                    width="60px"
                    onChange={onChange}
                    error={error?.message}
                    options={monthOptions}
                    value={value}
                  />
                )}
              />
              <Controller
                control={control}
                name="day"
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <Select
                    height="30px"
                    width="60px"
                    onChange={onChange}
                    error={error?.message}
                    options={dayOptions}
                    value={value}
                  />
                )}
              />
            </WrapperSelect>
          </>
        )}
        <div style={{ position: "relative" }}>
          {apiError && (
            <WrapperErrorMessage
              style={{
                left: 24,
              }}
            >
              <ErrorMessage left={154}>{apiError}</ErrorMessage>
            </WrapperErrorMessage>
          )}
          <Button
            name={typeForm === "full" ? "登　録" : "登録する"}
            background="#2F8CAE"
            color="#FFFFFF"
            border="none"
            fontSize={16}
            fontWeight={700}
            padding="7.5px 16px"
            lineHeight="25px"
            width="100%"
            style={{ height: 40 }}
            margin={typeForm === "full" ? "0px" : "28px 0 0"}
            disabled={
              typeForm === "full"
                ? !isDisabled || registeringPatient
                  ? true
                  : false
                : registeringPatient
            }
            loading={registeringPatient}
          />
        </div>
      </form>
    </div>
  );
};

export default AccountRegistration;
